/**
 * m3 echo Frontend - Onboarding Wizard
 *
 * Full-screen MUI Stepper that walks a newly provisioned user through:
 *   Step 0: Welcome screen
 *   Step 1: Profile setup (full name)
 *   Step 2: Create first project (editors/admins only; viewers skip)
 *   Step 3: Summary / Done
 *
 * Every step change is persisted to the backend via POST /api/onboarding/step.
 * "Überspringen" short-circuits the wizard via POST /api/onboarding/skip.
 * "Zum Dashboard" finalises via POST /api/onboarding/complete.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CelebrationOutlinedIcon from '@mui/icons-material/CelebrationOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { useAuthStore, useCanEdit } from '../stores/useAuthStore';
import { useUIStore } from '../stores/useUIStore';
import { onboardingApi } from '../services/onboarding';
import CreateProjectDialog from './CreateProjectDialog';

const STEP_LABELS = ['Willkommen', 'Profil', 'Erstes Projekt', 'Fertig'];

const OnboardingWizard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);
  const updateOnboardingCompleted = useAuthStore((state) => state.updateOnboardingCompleted);
  const updateOnboardingStep = useAuthStore((state) => state.updateOnboardingStep);
  const canEdit = useCanEdit();

  const { openCreateProjectDialog } = useUIStore();

  // Initialise from user.onboarding_step (falling back to 0) and clamp to valid range.
  const initialStep = useMemo(() => {
    const step = user?.onboarding_step ?? 0;
    return Math.max(0, Math.min(step, STEP_LABELS.length - 1));
  }, [user?.onboarding_step]);

  const [activeStep, setActiveStep] = useState<number>(initialStep);
  const [fullName, setFullName] = useState<string>(user?.full_name ?? '');
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Persist the active step index to the backend. Errors are surfaced as a
   * non-blocking Alert – progress is still tracked locally so the wizard
   * keeps flowing.
   */
  const persistStep = async (step: number) => {
    try {
      await onboardingApi.updateStep(step);
      updateOnboardingStep(step);
    } catch (err) {
      console.error('[OnboardingWizard] Failed to persist step:', err);
    }
  };

  /**
   * Advance to a specific step (skips step 2 for viewers).
   */
  const goToStep = async (nextStep: number) => {
    // Viewers skip the "Create first project" step in both directions.
    let target = nextStep;
    if (!canEdit && target === 2) {
      target = nextStep > activeStep ? 3 : 1;
    }
    setActiveStep(target);
    await persistStep(target);
  };

  const handleNext = async () => {
    setError(null);
    if (activeStep === 1) {
      // Persist the full_name locally so subsequent steps pre-fill it.
      // Backend update is not part of the onboarding contract yet; we
      // keep the value in the auth store so the UI reflects it immediately.
      if (user) {
        setUser({ ...user, full_name: fullName.trim() || user.full_name });
      }
    }
    await goToStep(activeStep + 1);
  };

  const handleBack = async () => {
    setError(null);
    await goToStep(Math.max(0, activeStep - 1));
  };

  const handleSkip = async () => {
    setBusy(true);
    setError(null);
    try {
      await onboardingApi.skip();
      updateOnboardingCompleted(true);
      navigate('/', { replace: true });
    } catch (err) {
      console.error('[OnboardingWizard] Skip failed:', err);
      setError('Onboarding konnte nicht übersprungen werden. Bitte erneut versuchen.');
    } finally {
      setBusy(false);
    }
  };

  const handleFinish = async () => {
    setBusy(true);
    setError(null);
    try {
      await onboardingApi.complete();
      updateOnboardingCompleted(true);
      navigate('/', { replace: true });
    } catch (err) {
      console.error('[OnboardingWizard] Complete failed:', err);
      setError('Onboarding konnte nicht abgeschlossen werden. Bitte erneut versuchen.');
    } finally {
      setBusy(false);
    }
  };

  const handleOpenProjectDialog = () => {
    openCreateProjectDialog();
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ textAlign: 'center', py: { xs: 2, sm: 4 } }}>
            <RocketLaunchOutlinedIcon
              sx={{ fontSize: { xs: 64, sm: 96 }, color: 'primary.main', mb: 2 }}
            />
            <Typography variant="h4" gutterBottom fontWeight={700}>
              Willkommen bei m3 echo
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 560, mx: 'auto', mb: 3 }}
            >
              m3 echo hilft Ihnen dabei, die Sichtbarkeit Ihrer Marke in generativen
              KI-Systemen zu messen, zu verstehen und kontinuierlich zu verbessern.
              In wenigen Schritten richten wir Ihr Konto ein.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto' }}>
              Sie können das Onboarding jederzeit überspringen und später über das Dashboard fortfahren.
            </Typography>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ py: { xs: 2, sm: 4 } }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <PersonOutlineOutlinedIcon
                sx={{ fontSize: { xs: 56, sm: 80 }, color: 'primary.main', mb: 1 }}
              />
              <Typography variant="h5" gutterBottom fontWeight={700}>
                Ihr Profil
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Wie sollen wir Sie ansprechen?
              </Typography>
            </Box>
            <Box sx={{ maxWidth: 480, mx: 'auto' }}>
              <TextField
                label="Vollständiger Name"
                fullWidth
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="z. B. Anna Schmidt"
                autoFocus
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1 }}
              >
                E-Mail: {user?.email}
              </Typography>
            </Box>
          </Box>
        );

      case 2:
        // Viewers should never land here - goToStep skips for them.
        return (
          <Box sx={{ py: { xs: 2, sm: 4 }, textAlign: 'center' }}>
            <FolderOpenOutlinedIcon
              sx={{ fontSize: { xs: 56, sm: 80 }, color: 'primary.main', mb: 1 }}
            />
            <Typography variant="h5" gutterBottom fontWeight={700}>
              Ihr erstes Projekt
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 560, mx: 'auto', mb: 3 }}
            >
              Legen Sie jetzt Ihr erstes Projekt an, um LLM-Sichtbarkeit für eine Marke
              oder einen Suchbegriff zu analysieren. Sie können diesen Schritt auch
              überspringen und später über das Dashboard ein Projekt erstellen.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={handleOpenProjectDialog}>
                Projekt erstellen
              </Button>
              <Button variant="outlined" onClick={() => goToStep(3)}>
                Später erstellen
              </Button>
            </Box>
          </Box>
        );

      case 3:
      default:
        return (
          <Box sx={{ py: { xs: 2, sm: 4 }, textAlign: 'center' }}>
            <CelebrationOutlinedIcon
              sx={{ fontSize: { xs: 72, sm: 104 }, color: 'success.main', mb: 2 }}
            />
            <Typography variant="h4" gutterBottom fontWeight={700}>
              Super, alles bereit!
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 560, mx: 'auto', mb: 1 }}
            >
              Ihr Konto ist einsatzbereit. Sie können jetzt Projekte erstellen,
              Tests starten und Ihre KI-Sichtbarkeit analysieren.
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 560, mx: 'auto' }}
            >
              Über das Menü erreichen Sie jederzeit alle Funktionen.
            </Typography>
          </Box>
        );
    }
  };

  // Visible stepper labels - viewers don't see "Erstes Projekt" as an active step
  // but we still render all labels so the layout stays consistent.
  const visibleSteps = STEP_LABELS;

  // When viewers are on step 1 and press next, they jump to step 3.
  // Disable the "Weiter"/"Zum Dashboard" button while the backend persists.
  const isLastStep = activeStep === 3;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: { xs: 2, sm: 4 },
      }}
    >
      <Paper
        elevation={isMobile ? 0 : 3}
        sx={{
          width: '100%',
          maxWidth: 720,
          p: { xs: 2, sm: 4 },
          borderRadius: 2,
        }}
      >
        <Stepper
          activeStep={activeStep}
          alternativeLabel={!isMobile}
          orientation={isMobile ? 'vertical' : 'horizontal'}
          sx={{ mb: 3 }}
        >
          {visibleSteps.map((label, idx) => (
            <Step
              key={label}
              completed={activeStep > idx}
              // Visually mark the project step as skipped for viewers so the
              // progression is obvious.
              {...(!canEdit && idx === 2 ? { disabled: true } : {})}
            >
              <StepLabel
                icon={idx === 3 ? <CheckCircleOutlineIcon /> : undefined}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ minHeight: 280 }}>{renderStep()}</Box>

        <Box
          sx={{
            mt: 3,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Button
            onClick={handleSkip}
            disabled={busy}
            color="inherit"
            sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
          >
            Überspringen
          </Button>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
            {activeStep > 0 && (
              <Button onClick={handleBack} disabled={busy} variant="text">
                Zurück
              </Button>
            )}
            {activeStep === 0 && (
              <Button variant="contained" onClick={handleNext} disabled={busy}>
                Los geht's
              </Button>
            )}
            {activeStep === 1 && (
              <Button variant="contained" onClick={handleNext} disabled={busy}>
                Weiter
              </Button>
            )}
            {activeStep === 2 && (
              // The primary actions for step 2 live inside the step body
              // (Projekt erstellen / Später erstellen). We still provide a
              // "Weiter" fallback for keyboard users.
              <Button variant="outlined" onClick={() => goToStep(3)} disabled={busy}>
                Weiter
              </Button>
            )}
            {isLastStep && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleFinish}
                disabled={busy}
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                Zum Dashboard
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* The inline project creation step reuses the existing dialog so the
          full project creation flow (prompts, search term, models, ...) is
          available without leaving the wizard. */}
      <CreateProjectDialog />
    </Box>
  );
};

export default OnboardingWizard;
